import React, { useEffect, useRef, useState } from 'react';
import { Camera, Utensils, Wine, PartyPopper, Users, Beer, ChevronDown, ChevronUp } from 'lucide-react';
import './Gallery.css';

const Gallery = () => {
  const galleryRef = useRef(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [expanded, setExpanded] = useState(false);
  const [columns, setColumns] = useState(3);

  // Track responsive column count to show exactly 2 rows when collapsed
  useEffect(() => {
    const computeCols = () => {
      const w = window.innerWidth;
      if (w <= 480) return 1;
      if (w <= 768) return 2;
      return 3;
    };
    const handle = () => setColumns(computeCols());
    handle();
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);

  // Collapse again whenever the category changes
  useEffect(() => {
    setExpanded(false);
  }, [activeCategory]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('scroll-animate');
          }
        });
      },
      { threshold: 0.1 }
    );

    const items = galleryRef.current?.querySelectorAll('.gallery-item');
    items?.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, [activeCategory, expanded, columns]);

  const categories = [
    { id: 'all', label: 'All', icon: Camera },
    { id: 'food', label: 'Food', icon: Utensils },
    { id: 'drinks', label: 'Drinks', icon: Wine },
    { id: 'events', label: 'Events', icon: PartyPopper },
    { id: 'customers', label: 'Happy Customers', icon: Users },
    { id: 'oregon', label: 'Made in Oregon — Beer & More', icon: Beer }
  ];

  const photos = [
    // Food
    {
      url: 'https://customer-assets.emergentagent.com/job_9c454374-0684-422d-9b1a-58060540f8e9/artifacts/cag0592x_43624E4E-23C9-44E3-A3BA-6DDFB5D9DAEB.png',
      title: 'Classic Cheeseburger',
      subtitle: 'Double Stacked',
      category: 'food'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_9c454374-0684-422d-9b1a-58060540f8e9/artifacts/vd2281hx_EFB6910E-EB61-45FC-854C-979AF78B151A.png',
      title: 'Bacon Bleu Burger',
      subtitle: 'With Crispy Fries',
      category: 'food'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_9c454374-0684-422d-9b1a-58060540f8e9/artifacts/evxbburt_IMG_8077.webp',
      title: 'Chicken Caesar Wrap',
      subtitle: 'Spinach Tortilla',
      category: 'food'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_9c454374-0684-422d-9b1a-58060540f8e9/artifacts/xz5nvcrh_0262E382-AF06-4C9E-B61D-90A7A64CB7E2.png',
      title: 'Southwest Egg Rolls',
      subtitle: 'Served with Salsa',
      category: 'food'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_elegant-royal-bar/artifacts/3quq6nwx_IMG_8082.webp',
      title: 'Buffalo Wings',
      subtitle: 'Crispy & Spicy',
      category: 'food'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_elegant-royal-bar/artifacts/32y42doi_IMG_8083.webp',
      title: 'Garlic Naan',
      subtitle: 'Fresh Baked',
      category: 'food'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_elegant-royal-bar/artifacts/9khjmbb0_IMG_8080.webp',
      title: 'Chicken Tenders',
      subtitle: 'Golden Perfection',
      category: 'food'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_2e2e3538-afb2-43be-b0a2-5d03e71fa1dd/artifacts/10mbooro_IMG_8081.webp',
      title: 'Fish & Chips',
      subtitle: 'Classic Favorite',
      category: 'food'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_elegant-royal-bar/artifacts/ylk2xvg0_IMG_8085.webp',
      title: 'Gulab Jamun',
      subtitle: 'Sweet Delight',
      category: 'food'
    },
    // Drinks
    {
      url: 'https://images.unsplash.com/photo-1597075687490-8f673c6c17f6?w=600',
      title: 'Signature Martini',
      subtitle: 'Crafted with Care',
      category: 'drinks'
    },
    {
      url: 'https://images.unsplash.com/photo-1596463989140-3b600dab72e5?w=600',
      title: 'Colorful Cocktails',
      subtitle: 'Happy Hour Favorites',
      category: 'drinks'
    },
    {
      url: 'https://images.unsplash.com/photo-1532269748385-a458d8ee473e?w=600',
      title: 'Cheers!',
      subtitle: 'Friends & Drinks',
      category: 'drinks'
    },
    {
      url: 'https://images.unsplash.com/photo-1671713682264-ecf27750b276?w=600',
      title: 'Bar Selection',
      subtitle: 'Premium Spirits',
      category: 'drinks'
    },
    // Events
    {
      url: 'https://customer-assets.emergentagent.com/job_9c454374-0684-422d-9b1a-58060540f8e9/artifacts/rylm2a8z_IMG_2794.jpeg',
      title: 'Open Mic Night',
      subtitle: 'Live Entertainment',
      category: 'events'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_9c454374-0684-422d-9b1a-58060540f8e9/artifacts/gpuiywjc_IMG_2792.jpeg',
      title: 'Comedy Night',
      subtitle: 'Laughs & Drinks',
      category: 'events'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_9c454374-0684-422d-9b1a-58060540f8e9/artifacts/sb1btwt4_IMG_2779.jpeg',
      title: 'Music Bingo',
      subtitle: 'Win Great Prizes',
      category: 'events'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_9c454374-0684-422d-9b1a-58060540f8e9/artifacts/91bpkvsa_IMG_2572.jpeg',
      title: 'Bingo Night',
      subtitle: 'Tuesdays & Thursdays',
      category: 'events'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_9c454374-0684-422d-9b1a-58060540f8e9/artifacts/be09lg2a_IMG_2563.jpeg',
      title: 'Jackpot Winners',
      subtitle: 'Xtreme Bar Bingo',
      category: 'events'
    },
    // Happy Customers
    {
      url: 'https://customer-assets.emergentagent.com/job_9c454374-0684-422d-9b1a-58060540f8e9/artifacts/rdfk7lz3_IMG_2453.jpeg',
      title: 'Paint Night',
      subtitle: 'Creativity & Cocktails',
      category: 'customers'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_9c454374-0684-422d-9b1a-58060540f8e9/artifacts/cx29mtw0_05BEE2FD-1B3F-4F8F-B539-BD9F54881026.png',
      title: 'Girls Night Out',
      subtitle: 'Good Times Together',
      category: 'customers'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_9c454374-0684-422d-9b1a-58060540f8e9/artifacts/yffxuk5i_IMG_1746.jpeg',
      title: 'Cheers!',
      subtitle: 'Royal Regulars',
      category: 'customers'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_9c454374-0684-422d-9b1a-58060540f8e9/artifacts/xvd1ml7s_IMG_1726.jpeg',
      title: 'Bar Nights',
      subtitle: 'Where Friends Meet',
      category: 'customers'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_9c454374-0684-422d-9b1a-58060540f8e9/artifacts/p3efwaih_IMG_9724.jpeg',
      title: 'Game Night',
      subtitle: 'Fun & Laughter',
      category: 'customers'
    },
    // Made in Oregon — Beer & More
    {
      url: 'https://customer-assets.emergentagent.com/job_9c454374-0684-422d-9b1a-58060540f8e9/artifacts/3q6chdf6_IMG_1920.jpeg',
      title: 'Great Notion',
      subtitle: 'Reeferm Madness & Blueberry Muffin',
      category: 'oregon'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_9c454374-0684-422d-9b1a-58060540f8e9/artifacts/x6w8x9bc_IMG_1219.jpeg',
      title: 'IPA Lineup',
      subtitle: 'Block 15, Wayfinder & ColdFire',
      category: 'oregon'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_9c454374-0684-422d-9b1a-58060540f8e9/artifacts/dhryrpgi_IMG_1092.jpeg',
      title: 'Block 15 Confluence',
      subtitle: 'Selection Series IPA',
      category: 'oregon'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_9c454374-0684-422d-9b1a-58060540f8e9/artifacts/meihjha4_IMG_0950.jpeg',
      title: 'Fresh Rotation',
      subtitle: 'Oregon Craft Selection',
      category: 'oregon'
    },
    {
      url: 'https://customer-assets.emergentagent.com/job_9c454374-0684-422d-9b1a-58060540f8e9/artifacts/svxrulu1_IMG_0928.jpeg',
      title: 'Tap House Favorites',
      subtitle: 'Local Brews on the Bar',
      category: 'oregon'
    }
  ];

  const filteredPhotos = activeCategory === 'all' 
    ? photos 
    : photos.filter(photo => photo.category === activeCategory);

  const visibleCount = columns * 2;
  const hasMore = filteredPhotos.length > visibleCount;
  const visiblePhotos = expanded ? filteredPhotos : filteredPhotos.slice(0, visibleCount);

  return (
    <section id="gallery" className="gallery-section">
      <div className="gallery-container">
        <div className="gallery-header section-header-animate">
          <Camera size={40} className="gallery-icon" />
          <h2 className="section-title">Gallery</h2>
          <p className="section-subtitle">Fresh. Authentic. Crafted with care.</p>
        </div>

        {/* Category Tabs */}
        <div className="gallery-categories" data-testid="gallery-categories">
          {categories.map((cat) => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.id}
                className={`category-tab ${activeCategory === cat.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat.id)}
                data-testid={`gallery-tab-${cat.id}`}
              >
                <Icon size={18} />
                <span>{cat.label}</span>
              </button>
            );
          })}
        </div>

        <div className="gallery-grid" ref={galleryRef}>
          {visiblePhotos.map((photo, idx) => (
            <div key={`${photo.category}-${idx}`} className="gallery-item hover-lift">
              <div className="gallery-image-wrapper">
                <img src={photo.url} alt={photo.title} className="gallery-image" />
                <div className="gallery-overlay">
                  <span className="gallery-category">{photo.subtitle}</span>
                  <h3 className="gallery-title">{photo.title}</h3>
                </div>
              </div>
            </div>
          ))}
        </div>

        {hasMore && (
          <div className="gallery-toggle-wrapper">
            <button
              className="gallery-toggle-btn"
              onClick={() => setExpanded((prev) => !prev)}
              data-testid="gallery-toggle-btn"
            >
              {expanded ? (
                <>
                  Show Less
                  <ChevronUp size={18} />
                </>
              ) : (
                <>
                  View More Photos
                  <ChevronDown size={18} />
                </>
              )}
            </button>
          </div>
        )}

        {filteredPhotos.length === 0 && (
          <div className="gallery-empty">
            <p>No photos in this category yet.</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default Gallery;
